import {
  CartProductCode,
  CurrentPayPlan,
  MSSMemberStatus,
  MembershipLevel,
  RoleType,
} from '@ace-clublabs/common-membership-api-types';
import {
  IGetConfirmationResponse,
  IPaymentInfo,
  IPriceCart,
  CallbackPaymentMethod,
  MSSProductStatus,
  MSSProduct,
} from '@lib/types';
import uuid4 from 'uuid4';
import CookieHelper from './cookieHelper';
import { IFormData, IMemberDetails } from '../state/model';
import {
  getAmountDue,
  getCartProductName,
  getIsDowngradingBenefitLevel,
  getIsUpgradingBenefitLevel,
  isAutopayBillPlan,
  isOnMonthlyBillPlan,
  isPastDue,
} from '../state/helpers';
import { isMonthlyPastDue } from './ruleHelper';
import { isGuid } from './stringHelper';
import LogCannon from '@ace-clublabs/log-cannon';
import { Pages } from './constants';
import { MembershipProductType } from '@ace-clublabs/common-membership-api-types';

export enum UtagType {
  UDO,
  view,
  link,
}

export const ABBREVIATED_MEMBERSHIP_LEVELS = {
  [MembershipLevel.CLASSIC]: 'clsc',
  [MembershipLevel.PLUS]: 'plus',
  [MembershipLevel.PREMIER]: 'prem',
  [MembershipLevel.CLASSIC_RV]: 'clrv',
  [MembershipLevel.PLUS_RV]: 'plrv',
  [MembershipLevel.PREMIER_RV]: 'prrv',
};

const ROLE_TYPE_TAGGING_MAP = {
  [RoleType.PRIMARY]: 'primary',
  [RoleType.ADULT_ASSOCIATE]: 'associate',
  [RoleType.DEPENDENT]: 'dependent',
  [RoleType.ASSOCIATE]: 'associate',
};

const PAYMENT_TRANSACTION_TAGGING_MAP = {
  [CallbackPaymentMethod.Echeck]: 'echeck',
  [CallbackPaymentMethod.CreditCard]: 'creditcard',
  [CallbackPaymentMethod.DebitCard]: 'debit',
  [CallbackPaymentMethod.Google]: 'google pay',
  [CallbackPaymentMethod.Apple]: 'apple pay',
};

export class UtagHelper {
  static loadUtagJs() {
    if ((global as any).utagLoadAttempt) return;
    (global as any).utagLoadAttempt = true;

    const utagJsScript = document.createElement('script');
    utagJsScript.src = '//tms.ace.aaa.com/ace/prod/utag.js';
    utagJsScript.type = 'text/javascript';
    utagJsScript.async = true;
    document.body.appendChild(utagJsScript);
  }

  static view(params: any) {
    (global as any).aceMediaTagValues = { ...params };
    if (typeof (global as any).utag === 'undefined') {
      UtagHelper.loadUtagJs();
    } else {
      (global as any).utag.view((global as any).aceMediaTagValues);
    }
  }

  static link(params: any) {
    (global as any).aceMediaTagValues = { ...params };
    if (typeof (global as any).utag === 'undefined') {
      UtagHelper.loadUtagJs();
    } else {
      (global as any).utag.link((global as any).aceMediaTagValues);
    }
  }

  static sendTag = (type: UtagType, tags: object | undefined, page: string, overrides?: object) => {
    const finalTags = {
      ...tags,
      ...overrides,
      page: `my account:manage my membership${page ? `:${page}` : ''}`,
    };
    switch (type) {
      case UtagType.view:
        UtagHelper.dispatchTntSpaEvent(finalTags);
        UtagHelper.view(finalTags);
        break;
      default:
        UtagHelper.link(finalTags);
        break;
    }
  };

  static buildTags(memberDetails: IMemberDetails, overrides?: object) {
    const { loggedInMember, inRenewal, inGrace, inArrears, members, currentBillPlan, benefitLevels } =
      memberDetails || {};

    const getFlow = () => {
      if (inRenewal) return 'renew';
      else if (inArrears) return 'arrears';
      else return 'midterm';
    };

    let tags: object = {
      campaigncodemr: memberDetails?.campaignCode,
      channel: 'my account',
      clubcode: memberDetails?.clubCode,
      custID: memberDetails?.loggedInCustomerId,
      flow: getFlow(),
      mem_associate: members
        ?.filter(
          (m) =>
            (m.roleType === RoleType.ADULT_ASSOCIATE || m.roleType === RoleType.ASSOCIATE) &&
            m.status == MSSMemberStatus.EXISTING
        )
        .length.toString(),
      mem_autopay: isAutopayBillPlan(currentBillPlan as CurrentPayPlan).toString(),
      mem_billplan: currentBillPlan?.toLowerCase(),
      mem_dependent: members
        ?.filter((m) => m.roleType === RoleType.DEPENDENT && m.status == MSSMemberStatus.EXISTING)
        .length.toString(),
      mem_level: benefitLevels?.existingLevel.toLowerCase(),
      mem_role: loggedInMember?.secured
        ? 'secured member'
        : loggedInMember?.roleType
        ? ROLE_TYPE_TAGGING_MAP[loggedInMember.roleType]
        : undefined,
      sessionid: LogCannon.getSessionId(),
      state: CookieHelper.getStateFromZipcodeCookie().toLowerCase(),
      subchannel1: 'manage my membership',
      ...overrides,
    };

    return tags;
  }

  static buildConfirmationTags(
    memberDetails: IMemberDetails,
    confirmation: IGetConfirmationResponse,
    paymentInfo: IPaymentInfo,
    priceCart: IPriceCart,
    overrides?: object
  ) {
    const {
      currentBenefitLevel,
      updatedBenefitLevel,
      paymentDetails,
      members,
      currentBillPlan,
      updatedBillPlan,
      confirmationId,
      tags,
      products = [],
    } = confirmation;
    const { inRenewal, termExpirationDate, inArrears } = memberDetails;

    const enrolledInAutopay = paymentDetails?.isAutoPayRequested && paymentDetails?.autoPaySuccessful;
    const productTags = UtagHelper.buildProductTags(priceCart, inRenewal, inArrears, tags, products);

    const benefitLevelChanged = updatedBenefitLevel && currentBenefitLevel !== updatedBenefitLevel;
    const billPlanChanged = updatedBillPlan && currentBillPlan !== updatedBillPlan;

    const memCurrent = getIsUpgradingBenefitLevel(currentBenefitLevel!, updatedBenefitLevel!)
      ? `upgrade:${ABBREVIATED_MEMBERSHIP_LEVELS[updatedBenefitLevel!]}`
      : getIsDowngradingBenefitLevel(currentBenefitLevel!, updatedBenefitLevel!)
      ? `downgrade:${ABBREVIATED_MEMBERSHIP_LEVELS[updatedBenefitLevel!]}`
      : updatedBenefitLevel?.toLowerCase();

    const isMonthlyBillPlan = isOnMonthlyBillPlan(currentBillPlan as CurrentPayPlan);
    const isADOPastExpiration = isPastDue(termExpirationDate!);
    const hasPaymentDue = getAmountDue(memberDetails, paymentInfo.monthlyPaymentData, priceCart) > 0;
    const isADOPastDue = isADOPastExpiration && hasPaymentDue;
    const hasPaymentPastDue = inArrears ? true : isMonthlyBillPlan ? isMonthlyPastDue(memberDetails) : isADOPastDue;

    let confirmationTags = {
      autopay: enrolledInAutopay ? 'new' : undefined,
      autopayconfirmation: enrolledInAutopay ? confirmationId : undefined,
      confirmation_id: confirmationId,
      error: tags?.error,
      managepaymenttype: tags?.managepaymenttype,
      mem_autopay: isAutopayBillPlan(
        billPlanChanged ? (updatedBillPlan as CurrentPayPlan) : (currentBillPlan as CurrentPayPlan)
      ).toString(),
      mem_associate: members
        ?.filter(
          (m) =>
            (m.roleType === RoleType.ADULT_ASSOCIATE || m.roleType === RoleType.ASSOCIATE) &&
            m.status !== MSSMemberStatus.REMOVED
        )
        .length.toString(),
      mem_billplan: billPlanChanged ? updatedBillPlan?.toLowerCase() : currentBillPlan?.toLowerCase(),
      mem_billplan_previous: billPlanChanged ? currentBillPlan?.toLowerCase() : undefined,
      mem_current: memCurrent,
      mem_dependent: members
        ?.filter((m) => m.roleType === RoleType.DEPENDENT && m.status !== MSSMemberStatus.REMOVED)
        .length.toString(),
      mem_level: benefitLevelChanged ? updatedBenefitLevel?.toLowerCase() : currentBenefitLevel?.toLowerCase(),
      mem_previous: benefitLevelChanged ? currentBenefitLevel?.toLowerCase() : undefined,
      mem_status_previous: hasPaymentPastDue ? 'paymentpastdue' : undefined,
      page: 'my account:manage my membership:confirmation',
      transaction_id: this.buildTransactionId(memberDetails?.inRenewal!),
      transaction_type: paymentDetails?.paymentType
        ? PAYMENT_TRANSACTION_TAGGING_MAP[paymentDetails.paymentType] //will need to add apple and google pay once supported
        : undefined,
      ...productTags,
      ...overrides,
    };
    return confirmationTags;
  }

  static buildProductTags(
    priceCart: IPriceCart,
    inRenewal: boolean | undefined,
    inArrears: boolean | undefined,
    confirmationTags: { [key: string]: string },
    confirmationProducts: MSSProduct[]
  ) {
    const priceCartItems = priceCart?.items;

    let productTags = {
      product: confirmationTags?.product || [],
      quantity: confirmationTags?.quantity || [],
      transaction_amount: confirmationTags?.transaction_amount || [],
      category: [],
    } as {
      product: string[];
      quantity: string[];
      transaction_amount: string[];
      category: string[];
    };

    const addProductTags = (productName: string, quantity: number, amount: number, discount: boolean = false) => {
      productTags.product.push(`${productName}${discount ? '_DISCOUNT' : ''}`); //will need to revisit logic for renewal discounts
      productTags.quantity.push(quantity.toString());
      productTags.transaction_amount.push(amount.toFixed(2));
    };

    priceCartItems?.forEach((item, i) => {
      const price = item?.price || 0;
      const code = item?.code || CartProductCode.Unknown;
      const productName = getCartProductName(code);

      if (code == CartProductCode.ASSOCIATE) {
        const description = item?.description.split('-');
        const newFamilyMember = isGuid(item?.id); // new family member have virtual id guid
        let roleTypeDesc = `${description[description.length - 1].trim().toUpperCase()}${
          newFamilyMember ? '-ADD' : ''
        }`; //adult, dependent, associate

        addProductTags(roleTypeDesc, 1, price);
      } else if (code == CartProductCode.RV) {
        addProductTags('RV-ADD', 1, price);
      } else {
        addProductTags(productName, 1, price);
      }
    });

    const isRVRemoved = confirmationProducts.some(
      ({ type, status }) => type === MembershipProductType.RV && status === MSSProductStatus.REMOVED
    );
    if (isRVRemoved) {
      addProductTags('RV-REMOVE', 1, 0);
    }

    if (priceCart?.salesTax && priceCart?.salesTax > 0) {
      addProductTags('SALESTAX', 1, priceCart?.salesTax);
    }

    productTags.category = productTags.category.concat(
      new Array(productTags.product.length).fill(`membership:${inRenewal || inArrears ? 'renew' : 'upgrade'}`)
    );

    return productTags;
  }

  static getIntcmpQueryParam(currentPage: Pages, cta?: string, targetPage?: Pages) {
    const getPageTag = (page: Pages) => {
      switch (page) {
        case Pages.HomePage:
          return 'managemymembership';
        case Pages.ManageMembershipPage:
          return 'managemembership';
        case Pages.ContactInfoPage:
          return 'editmemberinfo';
        case Pages.ChangeBenefitLevelPage:
          return 'selectbenefitlevel';
        case Pages.MembersDetailsPage:
          return 'memberdetails';
        case Pages.AddRVMotorcycle:
          return 'addrv';
        case Pages.AssociateForm:
          return 'editmemberinfo';
        default:
          return 'unknown';
      }
    };
    let currentPageTag = currentPage ? getPageTag(currentPage) : '';
    let targetPageTag = targetPage ? getPageTag(targetPage) : '';

    const targetTag = cta ? (targetPageTag ? `${cta}${targetPageTag}` : cta) : targetPageTag;
    return `?intcmp=${currentPageTag}:${targetTag}`;
  }

  static getAnalyticsSessionId() {
    const utagCookie = CookieHelper.getCookie('utag_main');
    if (utagCookie) {
      const parse = utagCookie.split('$ses_id:')[1];
      const sessionId = parse.split('%3')[0];
      return sessionId ?? '';
    }

    return '';
  }

  static buildTransactionId(inRenewal: boolean) {
    const guid = uuid4();
    const d = new Date();
    const date = `${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(
      2,
      '0'
    )}${d.getFullYear()}`;
    const prefix = inRenewal ? 'MR' : 'MU';
    return `${prefix}${date}${guid.substr(0, 12)}`;
  }

  static dispatchTntSpaEvent(tags: object) {
    document.dispatchEvent(
      new CustomEvent('tnt_spa', {
        detail: tags,
      })
    );
  }
}
